import { useEffect, useMemo, useState, type ReactNode, type MouseEvent } from 'react'
import {
  listCustomerUsage,
  type CustomerUsageResult,
  type CustomerUsageRow,
  type UsageStatus,
} from '../services/customerUsage'
import './CustomerUsage.css'

const STATUS_LABEL: Record<UsageStatus, string> = { active: 'Active', quiet: 'Quiet', dormant: 'Dormant' }
const STATUS_RANK: Record<UsageStatus, number> = { dormant: 0, quiet: 1, active: 2 }
const STATUS_COLOR: Record<UsageStatus, string> = { active: '#10b981', quiet: '#f59e0b', dormant: '#ef4444' }
const FEATURE_LABEL: Record<string, string> = { cockpit: 'Cockpit', eligibility: 'Eligibility Engine', roi: 'ROI Analytics', benefits_capture: 'Benefits Capture', collection: 'Collection Cards', patients: 'Patient Lookup', reports: 'Reports / Export', app: 'Session' }
const FEATURE_ORDER = ['cockpit', 'eligibility', 'collection', 'roi', 'benefits_capture', 'patients', 'reports']
const CORE_FEATURES = new Set(['cockpit', 'eligibility'])

// Hover explanations (native title) — what each metric means + how it's derived.
const TIP = {
  adoption: 'Adoption score (0–100): 40% daily engagement (active days ÷ operating days) + 30% team reach (active users ÷ seats) + 30% core-feature breadth (Cockpit + Eligibility). Δ = vs prior week.',
  avgDAU: 'Avg DAU: average distinct users active per operating day this week (sum of each user’s active-days ÷ operating days). Δ = vs prior week.',
  activeUsers: 'Distinct users active at least once this week, out of licensed seats.',
  daysActive: 'How many of the practice’s operating days had any activity this week.',
  checks: 'Eligibility / benefits checks run this week. Δ = vs prior week.',
  success: 'Share of this week’s checks that returned a clean, usable benefits result.',
  answered: 'Share of checks that came back actionable — a clean result OR a payer verdict (inactive coverage / member-not-found). The labor-saved number.',
  dollars: 'Patient-responsibility dollars surfaced by this week’s checks.',
  issues: 'Checks that caught something worth acting on — inactive coverage or out-of-network.',
  patients: 'Patient appointments this week. Δ = vs prior week.',
  clinics: 'Locations active this week / total. Amber means a clinic isn’t using it (uneven rollout).',
  lastSeen: 'Most recent recorded activity for the practice.',
  featureAdoption: 'Share of this week’s active users who opened each feature at least once. Core (Cockpit, Eligibility) drives the adoption score.',
}

const fmt = (n: number) => n.toLocaleString('en-US')
const money = (n: number) => (n === 0 ? '$0' : n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n}`)
const succClass = (s: number) => (s >= 90 ? 'good' : s >= 75 ? 'mid' : 'low')
const adoptColor = (s: number) => (s >= 70 ? '#059669' : s >= 40 ? '#d97706' : '#dc2626')

function personName(email: string): string {
  const local = (email.split('@')[0] || email)
  const name = local.split(/[._-]+/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ')
  return name || email
}
function relDay(iso: string | null): string {
  if (!iso) return '—'
  const d = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000)
  return d <= 0 ? 'Today' : d === 1 ? 'Yesterday' : `${d}d ago`
}
// Distinct 2-char badge from significant words (first letters), so practices
// that share a leading word ("Eye Focus" / "Eye 5" / "Eye Physicians") don't
// all collapse to the same "EY".
const INITIAL_STOP = new Set(['of', 'the', 'and', 'a', 'an', 'at', 'for', 'group', 'inc', 'llc'])
function initials(name: string): string {
  const words = name.replace(/[^A-Za-z0-9\s]/g, ' ').split(/\s+/)
    .filter((w) => w && !INITIAL_STOP.has(w.toLowerCase()))
  if (words.length === 0) return name.slice(0, 2).toUpperCase()
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}
function Wow({ now, prev, dec = 0 }: { now: number; prev: number; dec?: number }) {
  if (!prev) return null
  const d = Math.round(((now - prev) / prev) * 100)
  const cls = d > 0 ? 'up' : d < 0 ? 'down' : 'flat'
  return <span className={`cu-wow cu-wow--${cls}`}>{d > 0 ? '▲' : d < 0 ? '▼' : '±'}{Math.abs(d).toFixed(dec)}%</span>
}
// WoW as a percent, derived from current + absolute delta (prev = current - delta).
function Delta({ current, delta }: { current: number; delta: number | null }) {
  if (delta == null || delta === 0) return null
  const prev = current - delta
  if (prev <= 0) return null
  const pct = Math.round((delta / prev) * 100)
  if (pct === 0) return null
  const up = pct > 0
  return <span className={`cu-wow cu-wow--${up ? 'up' : 'down'}`}>{up ? '▲' : '▼'}{Math.abs(pct)}%</span>
}

// Value + delta in fixed-width slots so numbers align in one column and deltas in another.
function NumCell({ value, delta }: { value: ReactNode; delta?: ReactNode }) {
  return <span className="cu-numd"><span className="v">{value}</span><span className="d">{delta}</span></span>
}

// Shared floating-tooltip system — instant + visible on hover (native title was
// too subtle). Used by both the table and the drawer (separate components).
type TipState = { text: string; x: number; y: number } | null
function useTip() {
  const [tip, setTip] = useState<TipState>(null)
  const tipProps = (text: string) => ({
    onMouseEnter: (e: MouseEvent) => setTip({ text, x: e.clientX, y: e.clientY }),
    onMouseMove: (e: MouseEvent) => setTip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t)),
    onMouseLeave: () => setTip(null),
  })
  return { tip, tipProps }
}
function TipLayer({ tip }: { tip: TipState }) {
  if (!tip) return null
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
  return <div className="cu-tipfloat" style={{ left: Math.min(tip.x + 14, vw - 280), top: tip.y + 16 }}>{tip.text}</div>
}

type SortKey = 'default' | 'name' | 'status' | 'adoptionScore' | 'avgDAU' | 'checks' | 'successRate' | 'dollarsIdentified' | 'issuesCaught' | 'patients' | 'activeClinics' | 'lastActivity'
type Filter = 'all' | UsageStatus
type View = 'table' | 'map'

interface Props { onViewAs?: (orgId: string, name: string) => void }

export default function CustomerUsage({ onViewAs }: Props) {
  const [data, setData] = useState<CustomerUsageResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [view, setView] = useState<View>('table')
  const [sortKey, setSortKey] = useState<SortKey>('default')
  const [sortDir, setSortDir] = useState<1 | -1>(-1)
  const [selected, setSelected] = useState<CustomerUsageRow | null>(null)
  const [contacted, setContacted] = useState<Set<string>>(new Set())
  const { tip, tipProps } = useTip()

  async function load() {
    setLoading(true); setError(null)
    try { setData(await listCustomerUsage()) }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to load customer usage.') }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const rows = useMemo(() => {
    if (!data) return []
    const q = search.toLowerCase().trim()
    let arr = data.rows.filter((r) => r.name.toLowerCase().includes(q))
    if (filter !== 'all') arr = arr.filter((r) => r.status === filter)
    if (sortKey !== 'default') {
      arr = [...arr].sort((a, b) => {
        if (sortKey === 'name') return sortDir * a.name.localeCompare(b.name)
        if (sortKey === 'status') return sortDir * (STATUS_RANK[a.status] - STATUS_RANK[b.status])
        if (sortKey === 'lastActivity') return sortDir * ((Date.parse(a.lastActivity ?? '') || 0) - (Date.parse(b.lastActivity ?? '') || 0))
        const av = (a[sortKey] as number | null) ?? -1
        const bv = (b[sortKey] as number | null) ?? -1
        return sortDir * (av - bv)
      })
    }
    return arr
  }, [data, search, filter, sortKey, sortDir])

  function sortBy(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === 1 ? -1 : 1))
    else { setSortKey(k); setSortDir(k === 'name' ? 1 : -1) }
  }
  function toggleContacted(orgId: string) {
    setContacted((prev) => { const n = new Set(prev); if (n.has(orgId)) n.delete(orgId); else n.add(orgId); return n })
  }

  const s = data?.summary

  return (
    <div className="cu">
      <div className="cu-head">
        <div>
          <h1>Customer Usage</h1>
          <div className="cu-subt">Weekly engagement, results and direction across every practice{data ? ` · week of ${data.weekStart}` : ''}</div>
        </div>
        <div className="cu-head-meta">
          {data && <span>{data.practiceCount} practices</span>}
          <button className="cu-refresh" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      <div className="cu-kpis">
        <div className="cu-kpi"><div className="cu-kpi__l">Active this week</div><div className="cu-kpi__n">{s ? s.activeThisWeek : '—'}<span className="cu-kpi__l" style={{ fontSize: 13 }}>/{data?.practiceCount ?? 0}</span></div><div className="cu-kpi__d">at least one check run</div></div>
        <div className="cu-kpi"><div className="cu-kpi__l">Avg success rate</div><div className="cu-kpi__n">{s?.avgSuccessRate != null ? `${s.avgSuccessRate}%` : '—'}</div><div className="cu-kpi__d">clean eligibility results</div></div>
        <div className="cu-kpi"><div className="cu-kpi__l">Checks this week</div><div className="cu-kpi__n">{s ? fmt(s.totalChecks) : '—'}</div><div className="cu-kpi__d">across all practices</div></div>
        <div className="cu-kpi"><div className="cu-kpi__l">Patients this week</div><div className="cu-kpi__n">{s ? fmt(s.totalPatients) : '—'}</div><div className="cu-kpi__d">appointments</div></div>
        <div className={`cu-kpi${s && s.dormant > 0 ? ' cu-kpi--alert' : ''}`}><div className="cu-kpi__l">Needs outreach</div><div className="cu-kpi__n cu-kpi__n--bad">{s ? s.dormant + s.quiet : '—'}</div><div className="cu-kpi__d">{s ? `${s.dormant} dormant · ${s.quiet} quiet` : ''}</div></div>
      </div>

      <div className="cu-views">
        <button className={view === 'table' ? 'on' : ''} onClick={() => setView('table')}>▦ Table</button>
        <button className={view === 'map' ? 'on' : ''} onClick={() => setView('map')}>◈ Health map</button>
      </div>

      {view === 'table' && (
        <>
          <div className="cu-qf" style={{ marginTop: 14 }}>
            {(['all', 'dormant', 'quiet', 'active'] as Filter[]).map((f) => (
              <button key={f} className={`cu-chip${filter === f ? ' cu-chip--on' : ''}`} onClick={() => setFilter(f)}>{f === 'all' ? 'All' : STATUS_LABEL[f]}</button>
            ))}
            <div className="cu-qf__right"><input className="cu-search" placeholder="Search practices…" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
          </div>

          {error && <div className="cu-card"><div className="cu-empty"><div className="big">Couldn't load customer usage.</div><div>{error}</div><button className="cu-retry" onClick={load}>Retry</button></div></div>}
          {!error && loading && !data && <div className="cu-loading">Loading customer usage…</div>}

          {!error && data && (
            <div className="cu-card">
              <table className="cu-table">
                <thead>
                  <tr>
                    <th onClick={() => sortBy('name')}>Practice</th>
                    <th onClick={() => sortBy('status')}>Status</th>
                    <th className="r" {...tipProps(TIP.adoption)} onClick={() => sortBy('adoptionScore')}>Adoption</th>
                    <th className="r" {...tipProps(TIP.avgDAU)} onClick={() => sortBy('avgDAU')}>Avg DAU</th>
                    <th className="r" {...tipProps(TIP.checks)} onClick={() => sortBy('checks')}>Checks</th>
                    <th className="r" {...tipProps(TIP.success)} onClick={() => sortBy('successRate')}>Success</th>
                    <th className="r" {...tipProps(TIP.dollars)} onClick={() => sortBy('dollarsIdentified')}>$ Identified</th>
                    <th className="r" {...tipProps(TIP.issues)} onClick={() => sortBy('issuesCaught')}>Issues</th>
                    <th className="r" {...tipProps(TIP.patients)} onClick={() => sortBy('patients')}>Patients</th>
                    <th className="r" {...tipProps(TIP.clinics)} onClick={() => sortBy('activeClinics')}>Clinics</th>
                    <th className="r" {...tipProps(TIP.lastSeen)} onClick={() => sortBy('lastActivity')}>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td className="cu-empty" colSpan={11}>
                      <div className="big">{search ? `No practices match “${search}”.` : filter !== 'all' ? `No ${STATUS_LABEL[filter as UsageStatus].toLowerCase()} practices this week.` : 'No practices with activity.'}</div>
                      <div>{filter !== 'all' ? 'Nothing to chase in this bucket — good news.' : 'Try another filter or clear your search.'}</div>
                    </td></tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.orgId} className={selected?.orgId === r.orgId ? 'cu-sel' : ''} onClick={() => setSelected(r)}>
                      <td><div className="cu-practice">{r.name}{contacted.has(r.orgId) && <span title="Outreach sent" style={{ color: 'var(--cu-sage)', marginLeft: 6, fontWeight: 700 }}>✓</span>}</div><div className="cu-sub">{r.clinics} clinic{r.clinics === 1 ? '' : 's'} · {r.seats} seats</div></td>
                      <td><span className={`cu-status cu-status--${r.status}`}><span className="cu-d" />{STATUS_LABEL[r.status]}</span></td>
                      <td className="r" {...tipProps(TIP.adoption)}><NumCell value={r.activeUsers === 0 ? <span className="cu-muted">—</span> : <strong style={{ color: adoptColor(r.adoptionScore) }}>{r.adoptionScore}</strong>} delta={r.activeUsers === 0 ? null : <Delta current={r.adoptionScore} delta={r.adoptionDelta} />} /></td>
                      <td className="r" {...tipProps(TIP.avgDAU)}><NumCell value={r.activeUsers === 0 ? <span className="cu-muted">—</span> : r.avgDAU} delta={r.activeUsers === 0 ? null : <Delta current={r.avgDAU} delta={r.dauDelta} />} /></td>
                      <td className="r" {...tipProps(TIP.checks)}><NumCell value={r.checks === 0 ? <span className="cu-muted">0</span> : fmt(r.checks)} delta={<Wow now={r.checks} prev={r.checksPrev} />} /></td>
                      <td className="r" {...tipProps(TIP.success)}>{r.successRate == null ? <span className="cu-muted">—</span> : <span className={`cu-succ--${succClass(r.successRate)}`}>{r.successRate}%</span>}</td>
                      <td className="r" {...tipProps(TIP.dollars)}>{r.dollarsIdentified === 0 ? <span className="cu-muted">—</span> : money(r.dollarsIdentified)}</td>
                      <td className="r" {...tipProps(TIP.issues)}>{r.issuesCaught === 0 ? <span className="cu-muted">0</span> : r.issuesCaught}</td>
                      <td className="r" {...tipProps(TIP.patients)}><NumCell value={r.patients === 0 ? <span className="cu-muted">0</span> : fmt(r.patients)} delta={<Wow now={r.patients} prev={r.patientsPrev} />} /></td>
                      <td className="r" {...tipProps(TIP.clinics)}>{r.clinics <= 1 ? <span className="cu-muted">1/1</span> : <span style={r.activeClinics < r.clinics ? { color: 'var(--cu-amber)', fontWeight: 650 } : undefined}>{r.activeClinics}/{r.clinics}</span>}</td>
                      <td className="r cu-muted">{relDay(r.lastActivity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {view === 'map' && data && <HealthMap rows={data.rows} onSelect={setSelected} />}

      {selected && <Drawer row={selected} onClose={() => setSelected(null)} onViewAs={onViewAs} contacted={contacted.has(selected.orgId)} onToggleContacted={() => toggleContacted(selected.orgId)} />}
      <TipLayer tip={tip} />
    </div>
  )
}

function HealthMap({ rows, onSelect }: { rows: CustomerUsageRow[]; onSelect: (r: CustomerUsageRow) => void }) {
  const W = 860, H = 460, padL = 64, padR = 24, padT = 30, padB = 50
  const plotW = W - padL - padR, plotH = H - padT - padB
  const maxPat = Math.max(1, ...rows.map((r) => r.patients))
  const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v))
  const mapX = (v: number) => padL + (clamp(v, 0, 100) / 100) * plotW
  const mapY = (v: number) => padT + (1 - (clamp(v, 30, 102) - 30) / (102 - 30)) * plotH
  const xs = mapX(70), ys = mapY(90)

  return (
    <div className="cu-map">
      <svg viewBox={`0 0 ${W} ${H}`}>
        <rect x={padL} y={padT} width={plotW} height={plotH} fill="#fcfcfd" stroke="#e2e8f0" />
        <line x1={xs} y1={padT} x2={xs} y2={padT + plotH} stroke="#dfe3e8" strokeDasharray="5 4" />
        <line x1={padL} y1={ys} x2={padL + plotW} y2={ys} stroke="#dfe3e8" strokeDasharray="5 4" />
        <text x={padL + 10} y={padT + 17} fontSize="11" fontWeight="700" fill="#b9c0ca">ADOPTION GAP</text>
        <text x={padL + plotW - 10} y={padT + 17} textAnchor="end" fontSize="11" fontWeight="700" fill="#a9cdb6">★ CHAMPIONS</text>
        <text x={padL + 10} y={padT + plotH - 10} fontSize="11" fontWeight="700" fill="#e3b0b0">CHURN WATCH</text>
        <text x={padL + plotW - 10} y={padT + plotH - 10} textAnchor="end" fontSize="11" fontWeight="700" fill="#ddbd8a">USING · LOW ACCURACY</text>
        <text x={padL + plotW / 2} y={H - 12} textAnchor="middle" fontSize="12" fill="#64748b" fontWeight="600">Engagement — adoption score →</text>
        <text transform="rotate(-90)" x={-(padT + plotH / 2)} y={16} textAnchor="middle" fontSize="12" fill="#64748b" fontWeight="600">Value — eligibility success →</text>
        <text x={xs} y={padT + plotH + 16} textAnchor="middle" fontSize="10" fill="#94a3b8">70</text>
        <text x={padL - 8} y={ys + 3} textAnchor="end" fontSize="10" fill="#94a3b8">90%</text>
        {rows.map((r) => {
          const jx = ((r.orgId.charCodeAt(0) * 7) % 9) - 4
          const jy = ((r.orgId.charCodeAt(1 % r.orgId.length) * 5) % 9) - 4
          const cx = mapX(r.adoptionScore) + jx
          const cy = mapY(r.successRate ?? 0) + jy
          const rad = Math.max(7, 7 + (r.patients / maxPat) * 12)
          return (
            <g key={r.orgId} className="cu-dot" onClick={() => onSelect(r)}>
              <circle cx={cx} cy={cy} r={rad} fill={STATUS_COLOR[r.status]} fillOpacity={0.85} stroke="#fff" strokeWidth={2} />
              <text x={cx} y={cy + 3} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700" style={{ pointerEvents: 'none' }}>{initials(r.name)}</text>
            </g>
          )
        })}
      </svg>
      <div className="lg">
        <span><span className="sw" style={{ background: '#10b981' }} />Active</span>
        <span><span className="sw" style={{ background: '#f59e0b' }} />Quiet</span>
        <span><span className="sw" style={{ background: '#ef4444' }} />Dormant</span>
        <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>Bubble size = patient volume</span>
      </div>
      <div className="cu-mapnote">Adoption (x) × success rate (y) — top-right is healthy + accurate; bottom-right is using it but getting bad results. Click a bubble for the breakdown.</div>
    </div>
  )
}

function Drawer({ row: r, onClose, onViewAs, contacted, onToggleContacted }: {
  row: CustomerUsageRow; onClose: () => void; onViewAs?: (orgId: string, name: string) => void
  contacted: boolean; onToggleContacted: () => void
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const maxClinic = Math.max(1, ...r.clinicList.map((c) => c.checks + c.patients))
  const { tip, tipProps } = useTip()

  return (
    <>
      <div className="cu-overlay" onClick={onClose} />
      <aside className="cu-drawer" role="dialog" aria-label={`${r.name} usage`}>
        <div className="cu-drawer__top">
          <div>
            <h2 className="cu-drawer__clinic">{r.name}</h2>
            <div className="cu-drawer__meta">{r.clinics} clinic{r.clinics === 1 ? '' : 's'} · {r.seats} seats · last seen {relDay(r.lastActivity).toLowerCase()}</div>
            {r.contact && <div className="cu-contact">Contact: <b>{r.contact.name}</b> · {r.contact.role} · {r.contact.email}</div>}
            <div style={{ marginTop: 8 }}><span className={`cu-status cu-status--${r.status}`}><span className="cu-d" />{STATUS_LABEL[r.status]}</span></div>
          </div>
          <button className="cu-drawer__x" onClick={onClose}>✕</button>
        </div>

        <div className="cu-db">
          <div className="cu-dh">Engagement this week</div>
          <div className="cu-grid">
            <div className="cu-cell" {...tipProps(TIP.adoption)}><div className="cu-cell__l">Adoption</div><div className="cu-cell__n">{r.activeUsers === 0 ? '—' : <span style={{ color: adoptColor(r.adoptionScore) }}>{r.adoptionScore}</span>}<Delta current={r.adoptionScore} delta={r.adoptionDelta} /></div></div>
            <div className="cu-cell" {...tipProps(TIP.avgDAU)}><div className="cu-cell__l">Avg DAU</div><div className="cu-cell__n">{r.activeUsers === 0 ? '—' : r.avgDAU}<Delta current={r.avgDAU} delta={r.dauDelta} /></div></div>
            <div className="cu-cell" {...tipProps(TIP.activeUsers)}><div className="cu-cell__l">Active users</div><div className="cu-cell__n">{r.activeUsers}<span className="cu-muted" style={{ fontSize: 13 }}>/{r.seats}</span></div></div>
            <div className="cu-cell" {...tipProps(TIP.daysActive)}><div className="cu-cell__l">Days active</div><div className="cu-cell__n">{r.daysActive}<span className="cu-muted" style={{ fontSize: 13 }}>/{r.operatingDays}</span></div></div>
          </div>

          <div className="cu-dh">Results this week</div>
          <div className="cu-grid">
            <div className="cu-cell" {...tipProps(TIP.success)}><div className="cu-cell__l">Success rate</div><div className="cu-cell__n">{r.successRate == null ? '—' : <span className={`cu-succ--${succClass(r.successRate)}`}>{r.successRate}%</span>}</div></div>
            <div className="cu-cell" {...tipProps(TIP.checks)}><div className="cu-cell__l">Checks run</div><div className="cu-cell__n">{r.checks === 0 ? '0' : fmt(r.checks)}<Wow now={r.checks} prev={r.checksPrev} /></div></div>
            <div className="cu-cell" {...tipProps(TIP.dollars)}><div className="cu-cell__l">$ identified</div><div className="cu-cell__n">{money(r.dollarsIdentified)}</div></div>
            <div className="cu-cell" {...tipProps(TIP.issues)}><div className="cu-cell__l">Issues caught</div><div className="cu-cell__n">{r.issuesCaught}</div></div>
            <div className="cu-cell" {...tipProps(TIP.patients)}><div className="cu-cell__l">Patients</div><div className="cu-cell__n">{r.patients === 0 ? '0' : fmt(r.patients)}<Wow now={r.patients} prev={r.patientsPrev} /></div></div>
            <div className="cu-cell" {...tipProps(TIP.answered)}><div className="cu-cell__l">Answered</div><div className="cu-cell__n">{r.answeredRate == null ? '—' : `${r.answeredRate}%`}</div></div>
          </div>

          <div className="cu-dh" {...tipProps(TIP.featureAdoption)}>Feature adoption <span style={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: 'var(--cu-muted)' }}>% of active users</span></div>
          {r.activeUsers === 0 ? <div className="cu-stubbox">No recorded usage yet — fills in as the team uses the app.</div> : (
            FEATURE_ORDER.map((f) => {
              const pct = r.featureAdoption[f] ?? 0
              return (
                <div className="cu-feat" key={f}>
                  <div className="nm">{FEATURE_LABEL[f]}{CORE_FEATURES.has(f) && <span className="core">core</span>}</div>
                  <div className="bar"><span style={{ width: `${pct}%`, background: pct < 30 ? '#cbd5e1' : undefined }} /></div>
                  <div className="pct">{pct}%</div>
                </div>
              )
            })
          )}

          <div className="cu-dh">Who's using it <span style={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: 'var(--cu-muted)' }}>{r.activeUserList.length} active</span></div>
          {r.activeUserList.length === 0 ? <div className="cu-stubbox">No recorded users yet this week.</div> : (
            <div>{r.activeUserList.map((u) => (
              <div className="cu-userrow" key={u.name}>
                <div className="nm">{personName(u.name)}</div>
                <span className="badge">{Math.min(u.days, r.operatingDays)}/{r.operatingDays} days</span>
                <span className="last">{relDay(u.lastSeen + 'T12:00:00Z')}</span>
              </div>
            ))}</div>
          )}

          {r.clinics > 1 && (
            <>
              <div className="cu-dh">By clinic · {r.activeClinics}/{r.clinics} active</div>
              {r.clinicList.map((c) => (
                <div className="cu-clinic-row" key={c.clinicId}>
                  <div className="nm">{c.name}<div className="s">{c.operatingDays ? `${c.operatingDays}d open` : 'days n/a'}{c.timezone ? ` · ${c.timezone.split('/').pop()}` : ''}</div></div>
                  <div className="bar"><span style={{ width: `${Math.round(((c.checks + c.patients) / maxClinic) * 100)}%`, background: c.checks === 0 ? '#cbd5e1' : undefined }} /></div>
                  <div className="mt">{c.checks} chk · {c.patients} pt{c.successRate != null ? ` · ${c.successRate}%` : ''}</div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="cu-actions">
          <div className="row">
            <button className={`cu-btn${contacted ? ' cu-btn--on' : ''}`} onClick={onToggleContacted}>{contacted ? 'Outreach sent' : 'Mark outreach sent'}</button>
            <button className="cu-btn" disabled={!r.contact} title={r.contact ? `Email ${r.contact.email}` : 'No contact on file'} onClick={() => { if (r.contact) window.location.href = `mailto:${r.contact.email}?subject=${encodeURIComponent('VitalRev — ' + r.name)}` }}>{r.contact ? `Email ${r.contact.name.split(' ')[0]}` : 'No contact'}</button>
          </div>
          <button className="cu-btn cu-btn--primary" onClick={() => onViewAs?.(r.orgId, r.name)}>View as customer</button>
        </div>
      </aside>
      <TipLayer tip={tip} />
    </>
  )
}
